import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import AnimeCard from "@/components/base/AnimeCard";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareButton from "@/components/base/ShareButton";
import Toast from "@/components/base/Toast";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useToast } from "@/hooks/useToast";
import { findAnime, getAnimeCharacters, getRelatedAnimes } from "@/lib/data";
import CommentSection from "./components/CommentSection";

export default function DetailPage() {
  const { id } = useParams();
  const anime = findAnime(id);
  const { message, show } = useToast();
  const [showFullStory, setShowFullStory] = useState(false);

  useDocumentMeta({
    title: anime ? `日漫 - ${anime.title} 详情` : "日漫 - 动画详情",
    description: anime
      ? `${anime.title}（${anime.titleJa}）剧情简介：${anime.synopsis} 标签：${anime.tags.join("、")}。`
      : "日漫动画详情页，查看剧情简介、角色资料与相关推荐。",
    keywords: anime ? `${anime.title},${anime.categories.join(",")},日漫,日本动画` : "日漫,动画详情",
  });

  if (!anime) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-6 py-20">
          <EmptyState
            title="未找到这部动画"
            description="该作品可能已下架或链接有误，返回列表看看其他推荐吧。"
          />
          <div className="mt-6 text-center">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回动画列表
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const characters = getAnimeCharacters(anime.id);
  const related = getRelatedAnimes(anime, 4);

  return (
    <Layout>
      <section className="relative w-full h-[340px] md:h-[420px] overflow-hidden bg-foreground-950">
        <img
          src={anime.banner}
          alt={`${anime.title} 场景海报`}
          title={`${anime.title} 动画海报`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/60 to-foreground-950/20" />
      </section>

      <div className="w-full px-4 md:px-6 -mt-28 md:-mt-36 relative z-10">
        <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-8">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-52 shrink-0 -mt-24 md:-mt-28">
              <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border-4 border-background-50 bg-background-100 shadow-none">
                <img
                  src={anime.cover}
                  alt={`${anime.title} 动画封面`}
                  title={`${anime.title} 动画资源封面`}
                  className="w-full h-full object-top"
                />
              </div>
              <div className="mt-4 hidden md:flex flex-col gap-3">
                <FavoriteButton id={anime.id} kind="anime" />
                <ShareButton />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: "动画分类", to: "/category/all" },
                  { label: anime.categories[0], to: `/category/${encodeURIComponent(anime.categories[0])}` },
                  { label: anime.title },
                ]}
              />
              <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
                {anime.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{anime.titleJa}</p>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                <span className="flex items-center gap-1 text-accent-700 bg-accent-100 px-2.5 py-1 rounded-md font-semibold">
                  <i className="ri-star-fill" />
                  {anime.rating.toFixed(1)} 分
                </span>
                <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900">{anime.status}</span>
                {anime.categories.map((cat) => (
                  <Link
                    key={cat}
                    to={`/category/${encodeURIComponent(cat)}`}
                    className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {cat}
                  </Link>
                ))}
              </div>

              <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <dt className="text-foreground-500 text-xs">导演</dt>
                  <dd className="mt-1 font-medium text-foreground-900">{anime.director}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500 text-xs">制作公司</dt>
                  <dd className="mt-1 font-medium text-foreground-900">{anime.studio}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500 text-xs">播出年份</dt>
                  <dd className="mt-1 font-medium text-foreground-900">{anime.year} 年</dd>
                </div>
                <div>
                  <dt className="text-foreground-500 text-xs">集数</dt>
                  <dd className="mt-1 font-medium text-foreground-900">{anime.episodes} 集</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-foreground-500 text-xs">标签</dt>
                  <dd className="mt-1 flex flex-wrap gap-1.5">
                    {anime.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-md bg-primary-50 text-primary-700 text-xs">
                        #{tag}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap gap-3 md:hidden">
                <FavoriteButton id={anime.id} kind="anime" />
                <ShareButton />
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="w-full px-4 md:px-6 py-10 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">剧情简介</h2>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">{anime.synopsis}</p>
              <div className={`mt-4 text-sm md:text-base leading-relaxed text-foreground-700 ${showFullStory ? "" : "line-clamp-3"}`}>
                {anime.story}
              </div>
              <button
                type="button"
                onClick={() => setShowFullStory((prev) => !prev)}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
              >
                {showFullStory ? "收起剧情介绍" : "展开完整剧情介绍"}
                <i className={showFullStory ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </button>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">主要角色</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {characters.map((character) => (
                  <Link
                    key={character.id}
                    to={`/character/${character.id}`}
                    className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-background-100">
                      <img
                        src={character.avatar}
                        alt={`${character.name} 角色头像`}
                        title={`${character.name} 角色介绍`}
                        className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0 flex flex-col justify-center">
                      <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {character.name}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1">{character.nameJa}</p>
                      <p className="mt-1 text-xs text-foreground-600 line-clamp-2">{character.personality}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <CommentSection onToast={show} />
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">作品速览</h2>
              <ul className="space-y-3 text-sm text-foreground-700">
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">评分</span>
                  <span className="font-semibold text-accent-700">{anime.rating.toFixed(1)} / 10</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">分类</span>
                  <span className="font-medium">{anime.categories.join(" · ")}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">状态</span>
                  <span className="font-medium">{anime.status}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">集数</span>
                  <span className="font-medium">{anime.episodes} 集</span>
                </li>
              </ul>
              <p className="mt-4 pt-4 border-t border-secondary-200 text-xs leading-relaxed text-foreground-600">
                <i className="ri-information-line mr-1" />
                本站不提供在线播放与下载，页面内容仅用于作品介绍与资料展示。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">相关分类</h2>
              <div className="flex flex-wrap gap-2">
                {anime.categories.map((cat) => (
                  <Link
                    key={cat}
                    to={`/category/${encodeURIComponent(cat)}`}
                    className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {cat}动画
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-16">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">相关推荐</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {related.map((item) => (
            <AnimeCard key={item.id} anime={item} />
          ))}
        </div>
      </section>

      <Toast message={message} />
    </Layout>
  );
}